export class KiRefreshEvent {
  containerId: string;
  shortened: boolean;

  constructor(containerId: string, shortened: boolean) {
    this.containerId = containerId;
    this.shortened = shortened;
  }
}

/**
 * Nutzlast von `MOS_OHNE_WERTE_EVENT`: diese Karte hat ihre MOS-Datei bekommen, aber keine
 * ihrer Boxen hat einen Schlüssel hinein.
 *
 * Das ist bei den Lokalzeit-Karten für "Heute" und die Aussichtentage der Normalfall – die
 * MOS-Dateien kennen nur heute Nacht (`T_n_`), morgen Vormittag (`T_vm_`) und morgen
 * Nachmittag (`T_nm_`). Früher stand das nur im Debug-Log, und der Redakteur sah einen
 * Knopf, der scheinbar nichts tat. Ein Knopfdruck trifft viele Karten (Heute: 6 Zeilen × 3
 * Karten), deshalb meldet jede Karte einzeln und der Empfänger fasst zusammen.
 */
export class MosOhneWerte {
  constructor(
    /** `containerId` der Karte. */
    public readonly karteId: string,
    /** Zeilenüberschrift der Karte, z.B. "HEUTE: ganz". */
    public readonly zeile: string,
    /** Die geladene Datei, z.B. "05_Duesseldorf_Vorhersage.txt". */
    public readonly datei: string
  ) {
  }
}

/** Wie weit eine Grafik-Aktion reicht. */
export type GrafikUmfangArt = 'reiter' | 'zeile';

/**
 * Umfang einer Grafik-Aktion: ein ganzer Reiter, oder eine einzelne Karten-Zeile.
 *
 * Die Leiste über den Grafiken und die Knopfreihe an jeder Zeilenüberschrift tragen
 * dieselben Knöpfe; sie unterscheiden sich nur darin, **wie weit** sie reichen. Die
 * Leiste oben steht in **jedem** Reiter und meint dessen Zeilen – nicht das ganze
 * Dokument. Das ist keine Feinheit, sondern fachlich zwingend: jeder Reiter ist ein
 * anderer Zeitpunkt ("Heute", "2. Aussichtentag"), und eine KI-Anfrage, die die Zeiten
 * aller Reiter zusammenwirft, erzeugt Texte über den falschen Zeitraum.
 *
 * Adressiert wird über die `containerId`s der Karten und nie über die Beschriftung: die
 * Zeilen- und Reiternamen wiederholen sich zwischen den Bereichen ("HEUTE: ganz" gibt es
 * in NRW, in COSMO und in jeder Lokalzeit-Stadt), und einmal geöffnete Reiter bleiben
 * gerendert. `name` ist nur für Meldungen da.
 */
export class GrafikUmfang {
  constructor(
    /**
     * `containerId`s der Karten, auf die die Aktion wirken darf.
     *
     * `null` heißt "keine Einschränkung" und ist nur der Notausgang für ein seitenweites
     * Leeren – die Knöpfe der Anwendung schicken immer eine Liste.
     */
    public readonly kartenIds: string[] | null,
    /** Reicht die Aktion über einen Reiter oder über eine Zeile? */
    public readonly art: GrafikUmfangArt = 'zeile',
    /** Beschriftung des Umfangs (Reiter- bzw. Zeilenname), für Meldungen. */
    public readonly name: string = ''
  ) {
  }

  /**
   * Kennung dieses Umfangs – der Schlüssel, unter dem seine KI-Antwort liegt.
   *
   * Die Art gehört dazu: der Reiter-Umfang und der Umfang seiner **ersten** Zeile
   * beginnen mit derselben Karte, sind aber zwei verschiedene Antworten.
   */
  get schluessel(): string {
    return `${this.art}:${this.kartenIds?.[0] ?? ''}`;
  }

  /** Ohne Einschränkung – nur für ein seitenweites Leeren. */
  static alles(): GrafikUmfang {
    return new GrafikUmfang(null, 'reiter');
  }
}

/**
 * Nutzlast von "für COSMO übernehmen".
 *
 * Der Reiter allein genügte, solange der Knopf nur oben stand. Seit ihn auch jede
 * Zeilenüberschrift trägt, muss die Nutzlast sagen, ob eine einzelne Zeile gemeint ist.
 * Hier zählt der Zeilen**name** und nicht die `containerId`: die Übernahme sucht die
 * gleichnamige Zeile im COSMO-Reiter, und innerhalb eines Reiters sind die Namen eindeutig.
 */
/**
 * Nutzlast von `SYNC_PARTNER_EVENT`: der Sync-Schalter der Karte `quelleId` wurde umgelegt,
 * die Partnerkarte `partnerId` (feine ↔ sehr feine Unterteilung derselben Zeile) soll
 * nachziehen. `symbol` ist die Vorlage der Quelle – die Partnerkarte nimmt sie nur, wenn sie
 * selbst noch keine hat.
 */
export class SyncPartner {
  constructor(
    public readonly quelleId: string,
    public readonly partnerId: string,
    /** true = Sync einschalten, false = ausschalten und den vorherigen Stand zurückholen. */
    public readonly an: boolean,
    public readonly symbol: string | null
  ) {
  }
}

/** Nutzlast von WARNSCHILD_SPIEGEL_EVENT: welche Box welcher Karte welchen Wert bekommen hat ('' = geleert). */
export class WarnschildSpiegel {
  constructor(
    public readonly containerId: string,
    public readonly boxId: string,
    public readonly wert: string
  ) {
  }
}

export class CosmoUebernahme {
  constructor(
    /** Beschriftung des offenen Reiters, z.B. "Heute". */
    public readonly reiter: string,
    /** Beschriftung der Zeile, oder `null` für alle Zeilen des Reiters. */
    public readonly zeile: string | null = null
  ) {
  }
}
